/**
 * Phase 3 — Analytics avancés : Payroll export, Heatmap, Drilldown, At-Risk, Cost.
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Download, FileSpreadsheet, Flame, Microscope, AlertTriangle, DollarSign,
  TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const monthInput = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
})();

const fmtMoney = (v, cur = 'XOF') => new Intl.NumberFormat('fr-FR').format(v) + ' ' + cur;

// ─── Payroll Export ───
function PayrollExportTab() {
  const [month, setMonth] = useState(monthInput);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/leave/payroll/export?month=${month}&format=json`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const downloadXlsx = async () => {
    try {
      const r = await api.get(`/leave/payroll/export?month=${month}&format=xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `paie-absences-${month}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch (e) { toast.error('Erreur export'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label>Mois de paie</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payroll-month" />
        </div>
        <Button onClick={downloadXlsx} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-xlsx-btn">
          <FileSpreadsheet className="w-4 h-4 mr-2" />Télécharger Excel
        </Button>
      </div>

      {loading ? <p className="text-sm text-slate-400 text-center py-4">Chargement...</p> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Employés concernés</p>
              <p className="text-2xl font-bold">{data.totals.employees}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Jours payés</p>
              <p className="text-2xl font-bold text-emerald-600">{data.totals.paidDays}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Jours non payés</p>
              <p className="text-2xl font-bold text-red-600">{data.totals.unpaidDays}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Coût estimé</p>
              <p className="text-xl font-bold">{fmtMoney(data.totals.estimatedCost)}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Retenue estimée</p>
              <p className="text-xl font-bold text-red-600">{fmtMoney(data.totals.estimatedDeduction)}</p>
            </CardContent></Card>
          </div>

          {data.items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Aucune absence enregistrée sur la période.</p>
          ) : (
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="p-2">Matricule</th>
                    <th className="p-2">Employé</th>
                    <th className="p-2">Département</th>
                    <th className="p-2 text-right">Payés</th>
                    <th className="p-2 text-right">Non payés</th>
                    <th className="p-2 text-right">Coût</th>
                    <th className="p-2 text-right">Retenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(r => (
                    <tr key={r.employeeId} className="border-t" data-testid={`payroll-row-${r.employeeId}`}>
                      <td className="p-2 text-xs text-slate-500">{r.matricule || '—'}</td>
                      <td className="p-2">{r.firstName} {r.lastName}</td>
                      <td className="p-2 text-xs">{r.department || '—'}</td>
                      <td className="p-2 text-right">{r.totalPaidDays}</td>
                      <td className="p-2 text-right text-red-600">{r.totalUnpaidDays}</td>
                      <td className="p-2 text-right">{fmtMoney(r.estimatedCost)}</td>
                      <td className="p-2 text-right text-red-600">{fmtMoney(r.estimatedDeduction)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Heatmap ───
function HeatmapTab() {
  const [range, setRange] = useState(90);
  const [granularity, setGranularity] = useState('week');
  const [by, setBy] = useState('department');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/leave/analytics/heatmap?range_days=${range}&granularity=${granularity}&by=${by}`);
      setData(r.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [range, granularity, by]);

  useEffect(() => { load(); }, [load]);

  const max = data ? Math.max(1, ...data.series.flatMap(s => s.values)) : 1;
  const cell = (v) => {
    if (v === 0) return 'bg-slate-50';
    const ratio = v / max;
    if (ratio > 0.7) return 'bg-red-500 text-white';
    if (ratio > 0.4) return 'bg-orange-400 text-white';
    if (ratio > 0.15) return 'bg-amber-300';
    return 'bg-amber-100';
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap items-end">
        <Select value={by} onValueChange={setBy}>
          <SelectTrigger className="w-[200px]" data-testid="heatmap-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="department">Par département</SelectItem>
            <SelectItem value="leaveType">Par type d'absence</SelectItem>
          </SelectContent>
        </Select>
        <Select value={granularity} onValueChange={setGranularity}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Par jour</SelectItem>
            <SelectItem value="week">Par semaine</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          {[30, 90, 180, 365].map(r => (
            <Button key={r} size="sm" variant={range === r ? 'default' : 'outline'} onClick={() => setRange(r)}>
              {r}j
            </Button>
          ))}
        </div>
      </div>
      {loading ? <p className="text-sm text-slate-400 text-center py-4">Chargement...</p> : data && (
        data.series.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Aucune absence sur la période.</p>
        ) : (
          <Card><CardContent className="p-3 overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white p-1 text-left font-semibold">{by === 'department' ? 'Département' : 'Type'}</th>
                  {data.columns.map(c => (
                    <th key={c} className="p-1 text-center text-[9px] text-slate-500 whitespace-nowrap" style={{ minWidth: '24px' }}>
                      {c.slice(-5)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.series.map(s => (
                  <tr key={s.key} data-testid={`heatmap-row-${s.key}`}>
                    <td className="sticky left-0 bg-white p-1 pr-3 whitespace-nowrap text-xs font-medium border-r">
                      {s.label}
                    </td>
                    {s.values.map((v, i) => (
                      <td key={i} className={`text-center text-[10px] ${cell(v)}`} style={{ minWidth: '24px', height: '24px' }} title={`${data.columns[i]} : ${v} absent(s)`}>
                        {v > 0 ? v : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )
      )}
    </div>
  );
}

// ─── Drilldown ───
function DrilldownTab() {
  const [range, setRange] = useState(90);
  const [deptId, setDeptId] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const q = new URLSearchParams({ range_days: range });
      if (deptId !== 'all') q.append('department_id', deptId);
      const r = await api.get(`/leave/analytics/drilldown?${q}`);
      setData(r.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [range, deptId]);

  useEffect(() => {
    api.get('/departments').then(r => setDepartments(r.data?.departments || r.data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex gap-3 flex-wrap">
        <Select value={deptId} onValueChange={setDeptId}>
          <SelectTrigger className="w-[220px]" data-testid="drilldown-dept"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous départements</SelectItem>
            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          {[30, 90, 180, 365].map(r => (
            <Button key={r} size="sm" variant={range === r ? 'default' : 'outline'} onClick={() => setRange(r)}>{r}j</Button>
          ))}
        </div>
      </div>
      {loading ? <p className="text-sm text-slate-400 text-center py-4">Chargement...</p> : data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Effectif filtré</p>
              <p className="text-2xl font-bold">{data.totals.employees}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Jours absents</p>
              <p className="text-2xl font-bold">{data.totals.absentDays}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Événements non planifiés</p>
              <p className="text-2xl font-bold text-amber-600">{data.totals.unplanned}</p>
            </CardContent></Card>
          </div>
          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="p-2">Employé</th>
                  <th className="p-2">Département</th>
                  <th className="p-2 text-right">Jours absents</th>
                  <th className="p-2 text-right">Non planifiés</th>
                  <th className="p-2">Détail</th>
                </tr>
              </thead>
              <tbody>
                {data.items.filter(i => i.totalDays > 0 || i.unplannedCount > 0).slice(0, 50).map(i => (
                  <tr key={i.employeeId} className="border-t" data-testid={`drill-row-${i.employeeId}`}>
                    <td className="p-2">{i.firstName} {i.lastName}</td>
                    <td className="p-2 text-xs">{i.department || '—'}</td>
                    <td className="p-2 text-right font-medium">{i.totalDays}</td>
                    <td className="p-2 text-right">{i.unplannedCount > 0 ? <Badge className="bg-amber-100 text-amber-700">{i.unplannedCount}</Badge> : '—'}</td>
                    <td className="p-2 text-xs text-slate-500">
                      {Object.entries(i.byType).map(([k, v]) => `${k}: ${v}j`).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

// ─── At-Risk ───
function AtRiskTab() {
  const [range, setRange] = useState(90);
  const [threshold, setThreshold] = useState(15);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/leave/analytics/at-risk?range_days=${range}&threshold_rate=${threshold}`);
      setData(r.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [range, threshold]);

  useEffect(() => { load(); }, [load]);

  const riskBadge = (lvl) => ({
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-blue-100 text-blue-700',
  }[lvl] || 'bg-slate-100');

  const TrendIcon = ({ t }) => t === 'rising' ? <TrendingUp className="w-3.5 h-3.5 text-red-600" /> :
    t === 'decreasing' ? <TrendingDown className="w-3.5 h-3.5 text-emerald-600" /> :
    <Minus className="w-3.5 h-3.5 text-slate-400" />;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap items-end">
        <div>
          <Label>Seuil absentéisme %</Label>
          <Input type="number" min={0} max={100} value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
            className="w-24" data-testid="risk-threshold" />
        </div>
        <div className="flex gap-1">
          {[30, 90, 180, 365].map(r => (
            <Button key={r} size="sm" variant={range === r ? 'default' : 'outline'} onClick={() => setRange(r)}>{r}j</Button>
          ))}
        </div>
      </div>
      {loading ? <p className="text-sm text-slate-400 text-center py-4">Chargement...</p> :
        data && data.count === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-slate-500">
            ✓ Aucun collaborateur à risque détecté sur la période.
          </CardContent></Card>
        ) : data && (
          <div className="space-y-2">
            {data.items.map(i => (
              <Card key={i.employeeId} data-testid={`risk-${i.employeeId}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <strong>{i.firstName} {i.lastName}</strong>
                        <Badge className={riskBadge(i.riskLevel)}>
                          Risque {i.riskLevel === 'high' ? 'élevé' : i.riskLevel === 'medium' ? 'modéré' : 'faible'} · {i.riskScore}/100
                        </Badge>
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <TrendIcon t={i.trend} />
                          {i.trend === 'rising' ? 'haussière' : i.trend === 'decreasing' ? 'baissière' : 'stable'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500">{i.jobTitle || '—'}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Taux d'absentéisme : <strong>{i.absenceRatePercent}%</strong> · {i.totalDays} j d'absence · {i.unplannedCount} événement(s) non planifié(s)
                      </p>
                      <div className="mt-1.5 space-y-0.5">
                        {i.riskFactors.map((f, idx) => (
                          <p key={idx} className="text-xs text-amber-700">⚠ {f}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ─── Cost ───
function CostTab() {
  const [range, setRange] = useState(90);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/leave/analytics/cost-estimate?range_days=${range}`).then(r => setData(r.data));
  }, [range]);

  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Chargement...</p>;
  const maxType = Math.max(1, ...data.byType.map(t => t.days));

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {[30, 90, 180, 365].map(r => (
          <Button key={r} size="sm" variant={range === r ? 'default' : 'outline'} onClick={() => setRange(r)}>{r}j</Button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500">Coût total estimé</p>
          <p className="text-2xl font-bold">{fmtMoney(data.estimatedTotalCost, data.currency)}</p>
          <p className="text-xs text-slate-500 mt-1">sur {data.totalDays} jours</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500">Retenue estimée</p>
          <p className="text-2xl font-bold text-red-600">{fmtMoney(data.estimatedDeduction, data.currency)}</p>
          <p className="text-xs text-slate-500 mt-1">{data.unpaidDays} j non payés</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500">Coût journalier moyen</p>
          <p className="text-2xl font-bold">{fmtMoney(data.averageDailyCost, data.currency)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500">Jours payés</p>
          <p className="text-2xl font-bold text-emerald-600">{data.paidDays}</p>
        </CardContent></Card>
      </div>
      <Card><CardContent className="p-4">
        <h3 className="font-semibold mb-3">Coût par type d'absence</h3>
        {data.byType.length === 0 ? (
          <p className="text-sm text-slate-400">Aucune donnée.</p>
        ) : data.byType.map(t => (
          <div key={t.name} className="mb-2" data-testid={`cost-type-${t.name}`}>
            <div className="flex justify-between text-xs mb-1"><span>{t.name}</span><strong>{t.days} j</strong></div>
            <div className="h-2 bg-slate-100 rounded">
              <div className="h-2 bg-indigo-500 rounded" style={{ width: `${(t.days / maxType) * 100}%` }} />
            </div>
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}

// ─── Main Page ───
export default function AbsencesAnalyticsPage() {
  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="absences-analytics-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Microscope className="w-6 h-6 text-indigo-600" />
            Analytics Absences
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Export paie, heatmaps, drill-down, détection collaborateurs à risque, coût estimé.
          </p>
        </div>
        <Tabs defaultValue="payroll">
          <TabsList className="bg-slate-100 flex-wrap h-auto">
            <TabsTrigger value="payroll" data-testid="tab-payroll">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Export paie
            </TabsTrigger>
            <TabsTrigger value="heatmap" data-testid="tab-heatmap">
              <Flame className="w-4 h-4 mr-2" />Heatmap
            </TabsTrigger>
            <TabsTrigger value="drilldown" data-testid="tab-drilldown">
              <Microscope className="w-4 h-4 mr-2" />Drill-down
            </TabsTrigger>
            <TabsTrigger value="at-risk" data-testid="tab-at-risk">
              <AlertTriangle className="w-4 h-4 mr-2" />Collaborateurs à risque
            </TabsTrigger>
            <TabsTrigger value="cost" data-testid="tab-cost">
              <DollarSign className="w-4 h-4 mr-2" />Coût estimé
            </TabsTrigger>
          </TabsList>
          <TabsContent value="payroll" className="mt-4"><PayrollExportTab /></TabsContent>
          <TabsContent value="heatmap" className="mt-4"><HeatmapTab /></TabsContent>
          <TabsContent value="drilldown" className="mt-4"><DrilldownTab /></TabsContent>
          <TabsContent value="at-risk" className="mt-4"><AtRiskTab /></TabsContent>
          <TabsContent value="cost" className="mt-4"><CostTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
